"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import { Check, RefreshCw, X } from "lucide-react";

const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const push = useCallback((toast) => {
    const id = ++idRef.current;
    setToasts((prev) => [...prev, { id, kind: "success", ...toast }]);
    // duration: null means "stays until the user acts" -- e.g. the new-
    // version notice, which shouldn't vanish before someone sees it.
    if (toast.duration !== null) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, toast.duration ?? 3200);
    }
    return id;
  }, []);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ push, dismiss }}>
      {children}
      <ToastStack toasts={toasts} dismiss={dismiss} />
    </ToastContext.Provider>
  );
}

// Stable no-op fallback (used when no provider is mounted, e.g. in unit tests).
// Must be a module constant, not a fresh object per call — a new identity each
// render would re-fire effects that depend on `push`.
const NOOP_TOAST = { push: () => {}, dismiss: () => {} };

export function useToast() {
  return useContext(ToastContext) ?? NOOP_TOAST;
}

const KIND_ICON = {
  error: <X className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[var(--qk-clay)]" />,
  info: <RefreshCw className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[var(--qk-sky)]" />,
  success: <Check className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[var(--qk-moss)]" />,
};

function ToastStack({ toasts, dismiss }) {
  if (!toasts.length) return null;
  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-[100] flex flex-col gap-2">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={
            "pointer-events-auto min-w-[220px] max-w-[320px] rounded-lg border px-3 py-2 text-xs shadow-lg backdrop-blur " +
            (t.kind === "error"
              ? "border-[color-mix(in_srgb,var(--qk-clay)_40%,transparent)] bg-[color-mix(in_srgb,var(--qk-clay)_12%,var(--qk-paper-3))] text-[var(--qk-clay)]"
              : "border-[var(--qk-rule)] bg-[var(--qk-paper-3)] text-[var(--qk-ink)]")
          }
        >
          <div className="flex items-start gap-2">
            {KIND_ICON[t.kind] ?? KIND_ICON.success}
            <div className="flex-1">
              <div className="font-medium">{t.title}</div>
              {t.description && (
                <div className="mt-0.5 text-[11px] opacity-75">{t.description}</div>
              )}
              {t.action && (
                <button
                  type="button"
                  onClick={t.action.onClick}
                  className="mt-1.5 rounded border border-current px-2 py-0.5 text-[11px] font-medium opacity-90 hover:opacity-100"
                >
                  {t.action.label}
                </button>
              )}
            </div>
            {/* Only the ones that stay: a toast that leaves on its own does
                not need a way out, and an X on every one is noise. */}
            {t.duration === null && (
              <button
                type="button"
                onClick={() => dismiss(t.id)}
                aria-label="Dismiss"
                className="-mr-1 -mt-0.5 flex-shrink-0 rounded p-0.5 opacity-50 hover:opacity-100"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
